import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import WorkspaceApi from './workspace.api'
import { useDefaultWorkspaceStore, useSelectedWorkspaceStore } from './workspace.store'
import { useAuthStore } from '../auth'

export const useWorkspaces = () => {
  const { selectedTenant } = useAuthStore()
  
  return useQuery({
    queryKey: ['workspaces', selectedTenant?.id],
    queryFn: WorkspaceApi.getAllWorkspaces,
    enabled: !!selectedTenant,
  })
}

export const useGetAllWorkspaces = useWorkspaces

export const useWorkspace = (id?: string) => {
  return useQuery({
    queryKey: ['workspace', id],
    queryFn: () => WorkspaceApi.getWorkspaceById(id),
    enabled: !!id,
  })
}

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient()
  const { selectedTenant } = useAuthStore()

  return useMutation({
    mutationFn: WorkspaceApi.createWorkspace,
    onSuccess: () => {
      message.success('Workspace created successfully')
      // Invalidate workspaces query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['workspaces', selectedTenant?.id] })
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to create workspace')
    },
  })
}

export const useJoinWorkspace = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setDefaultWorkspace } = useDefaultWorkspaceStore()
  const { setSelectedWorkspace } = useSelectedWorkspaceStore()

  return useMutation({
    mutationFn: (workspaceId: string) => WorkspaceApi.joinWorkspace(workspaceId),
    onSuccess: (data) => {
      if (data) {
        // Set both default and selected workspace
        setDefaultWorkspace(data)
        setSelectedWorkspace(data)
        
        message.success('Workspace selected successfully')
        
        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ['workspaces'] })
        
        // Navigate to QA Chat
        navigate(`/workspaces/${data.id}/qa-chat`)
      }
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to join workspace')
    },
  })
}
